import { useCallback, useMemo } from "react";
import { FinancialDocumentListItem } from "@common/types/financialDocument";

interface Params {
    documents: FinancialDocumentListItem[];
    shownDocumentId: number | null;
    showDocumentDetails: (financialDocumentId: number) => void;
}

export function useDocumentDetailsNavigation({
    documents,
    shownDocumentId,
    showDocumentDetails,
}: Params) {
    const currentIndex = useMemo(() => {
        if (!shownDocumentId) {
            return -1;
        }
        return documents.findIndex(
            (document) => document.id === shownDocumentId,
        );
    }, [documents, shownDocumentId]);

    const canGoToPrevious = currentIndex > 0;
    const canGoToNext =
        currentIndex !== -1 && currentIndex < documents.length - 1;
    const shouldShowNavigation = documents.length > 1 && currentIndex !== -1;

    const goToPrevious = useCallback(() => {
        if (!canGoToPrevious) {
            return;
        }
        const previousDocument = documents[currentIndex - 1];
        showDocumentDetails(previousDocument.id);
    }, [canGoToPrevious, currentIndex, documents, showDocumentDetails]);

    const goToNext = useCallback(() => {
        if (!canGoToNext) {
            return;
        }
        const nextDocument = documents[currentIndex + 1];
        showDocumentDetails(nextDocument.id);
    }, [canGoToNext, currentIndex, documents, showDocumentDetails]);

    return {
        canGoToPrevious,
        canGoToNext,
        shouldShowNavigation,
        goToPrevious,
        goToNext,
    };
}
